import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useFirstOrg } from "../hooks/use-orgs.js";
import { useCreateWebsite } from "../hooks/use-websites.js";
import { AppShell } from "../components/app-shell.js";
import { Button } from "../components/button.js";
import { FormField } from "../components/form-field.js";

export function AddWebsitePage() {
  const navigate = useNavigate();
  const org = useFirstOrg();
  const createWebsite = useCreateWebsite(org?.id ?? "");

  const [url, setUrl] = useState("https://");
  const [urlError, setUrlError] = useState<string | null>(null);

  function validateUrl(value: string): string | null {
    try {
      const parsed = new URL(value);
      if (!["http:", "https:"].includes(parsed.protocol)) {
        return "URL must start with http:// or https://";
      }
      return null;
    } catch {
      return "Please enter a valid URL, e.g. https://example.com";
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const err = validateUrl(url.trim());
    if (err) {
      setUrlError(err);
      return;
    }
    setUrlError(null);

    try {
      const result = await createWebsite.mutateAsync(url.trim());
      void navigate(`/wizard/${result.website.id}`);
    } catch {
      // Error is displayed via createWebsite.isError
    }
  }

  if (!org) {
    return (
      <AppShell>
        <div className="alert alert--error">
          No organisation found. Please{" "}
          <Link to="/setup/org">set up your organisation</Link> first.
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <h1 className="page-title">Add your website</h1>
      <p className="page-subtitle">
        Enter the full URL of the website you want to monitor.
      </p>

      {createWebsite.error && (
        <div className="alert alert--error" role="alert">
          {createWebsite.error.message}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate style={{ maxWidth: 480 }}>
        <FormField
          id="url"
          label="Website URL"
          required
          error={urlError ?? undefined}
          hint="Include the full address, e.g. https://example.com"
        >
          <input
            id="url"
            type="url"
            className="form-field__input"
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              if (urlError) setUrlError(null);
            }}
            onBlur={() => {
              const trimmed = url.trim();
              if (trimmed && trimmed !== "https://") {
                const err = validateUrl(trimmed);
                if (err) setUrlError(err);
              }
            }}
            placeholder="https://example.com"
            required
            aria-invalid={urlError ? true : undefined}
            aria-describedby={urlError ? "url-error" : undefined}
          />
        </FormField>

        <Button
          type="submit"
          loading={createWebsite.isPending}
          disabled={!url.trim() || url === "https://"}
        >
          Add website
        </Button>
      </form>
    </AppShell>
  );
}
